export const BUTTONS = ['claim_opened', 'couldnt_open', 'put_note_claim', 'fix_policy_refire', 'send_ai', 'text_client', 'add_def', 'confirm_no3p',
    'enter_carrier', 'enter_phone', 'on_it', 'chatter_team', 'confirm_no3p_hitrun'];
export const MASTER_SWITCH = 'console.writes';
export const BUTTON_SWITCH = Object.fromEntries(BUTTONS.map((b) => [b, `button.${b}`]));
export const COULDNT_OPEN = ['wrong', 'nocov', 'already', 'more', 'other'];
const SF_ID = /^[A-Za-z0-9]{15}(?:[A-Za-z0-9]{3})?$/;
const text = (v, max = 120) => typeof v === 'string' && v.trim().length > 0 && v.trim().length <= max;
export const MATTER_LEVEL = ['text_client', 'add_def', 'chatter_team', 'confirm_no3p_hitrun'];
export const CHATTER_MAX_MENTIONS = 10;
export const CHATTER_MAX_CHARS = 1000;
export function validateRequest(r) {
    if (!BUTTONS.includes(r.button))
        return { ok: false, code: 'unknown_button' };
    const b = r.button;
    if (typeof r.matterId !== 'string' || !SF_ID.test(r.matterId))
        return { ok: false, code: 'bad_matter_id' };
    if (r.insuranceId === null || r.insuranceId === undefined) {
        if (!MATTER_LEVEL.includes(b))
            return { ok: false, code: 'insurance_required' };
    }
    else if (typeof r.insuranceId !== 'string' || !SF_ID.test(r.insuranceId))
        return { ok: false, code: 'bad_insurance_id' };
    const i = (r.input && typeof r.input === 'object' ? r.input : {});
    switch (b) {
        case 'claim_opened':
        case 'put_note_claim': return text(i.claim, 60) ? { ok: true } : { ok: false, code: 'claim_required' };
        case 'fix_policy_refire': return text(i.policy, 60) ? { ok: true } : { ok: false, code: 'policy_required' };
        case 'enter_carrier':
        case 'add_def': return text(i.carrier) ? { ok: true } : { ok: false, code: 'carrier_required' };
        case 'enter_phone': return text(i.phone, 30) ? { ok: true } : { ok: false, code: 'phone_required' };
        case 'chatter_team': {
            if (!text(i.message, CHATTER_MAX_CHARS))
                return { ok: false, code: 'message_required' };
            const who = i.mentions;
            if (!Array.isArray(who) || who.length === 0)
                return { ok: false, code: 'pick_someone' };
            if (who.length > CHATTER_MAX_MENTIONS)
                return { ok: false, code: 'too_many_mentions' };
            if (!who.every((u) => typeof u === 'string' && /^005[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/.test(u)) || new Set(who.map((u) => String(u).slice(0, 15))).size !== who.length)
                return { ok: false, code: 'bad_mention' };
            return { ok: true };
        }
        case 'confirm_no3p': return i.policeSaysNone === true && i.sr19SaysNone === true ? { ok: true } : { ok: false, code: 'both_boxes_required' };
        // hit and run (Neila 2026-10-08): the client advocate's own decision, ticked by the advocate
        case 'confirm_no3p_hitrun': return i.advocateConfirms === true ? { ok: true } : { ok: false, code: 'advocate_confirmation_required' };
        case 'couldnt_open': {
            if (!COULDNT_OPEN.includes(i.why))
                return { ok: false, code: 'bad_reason' };
            if (i.why === 'wrong' && !text(i.carrier))
                return { ok: false, code: 'carrier_required' };
            if (i.why === 'already' && !text(i.claim, 60))
                return { ok: false, code: 'claim_required' };
            if (i.why === 'more' && !text(i.what, 200))
                return { ok: false, code: 'what_required' };
            if (i.why === 'other' && !text(i.note, 300))
                return { ok: false, code: 'note_required' };
            return { ok: true };
        }
        default: return { ok: true };
    }
}
const pacificDate = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
/** "Text client again" at most once a Pacific day per client (Rod 2026-10-06 "3a"); only texts that went (or would have gone) count. */
export function textClientAllowed(history, clientId, now) {
    if (!clientId)
        return true;
    const today = pacificDate(now);
    return !history.some((h) => h.button === 'text_client' && h.clientId === clientId && (h.result === 'dry_run' || h.result === 'accepted')
        && pacificDate(new Date(h.at)) === today);
}
/** An outcome hands the row back (or settles it): the "on it" marker clears. Starting a call or texting does not. */
export function outcomeClearsOnIt(b) {
    return !['on_it', 'text_client'].includes(b);
}
/** Shared Salesforce users that stand for a whole desk ("Accounting Lead", "FLR Lead"): listed, never pre-ticked. */
const DESK_USER = /\sLead$/i;
/** The matter team for the Chatter button: one line per person (a person with two roles shows once, roles joined), desk users last.
 * Pre-ticked: the Claims-role member(s) and the case manager, the people who open claims on the matter. */
export function chatterTeam(members, caseManager) {
    const by = new Map();
    for (const m of members) {
        if (!m.userId || !m.name)
            continue;
        const e = by.get(m.userId);
        if (e) {
            if (m.role && !e.roles.includes(m.role))
                e.roles.push(m.role);
        }
        else
            by.set(m.userId, { userId: m.userId, name: m.name, roles: m.role ? [m.role] : [] });
    }
    const cm = (caseManager ?? '').trim().toLowerCase();
    return [...by.values()].map((e) => ({
        userId: e.userId, name: e.name, role: e.roles.join(', '),
        picked: !DESK_USER.test(e.name) && (e.roles.some((r) => r.toLowerCase() === 'claims') || (!!cm && e.name.trim().toLowerCase() === cm)),
    })).sort((a, b) => Number(DESK_USER.test(a.name)) - Number(DESK_USER.test(b.name)) || Number(b.picked) - Number(a.picked) || a.name.localeCompare(b.name));
}
//# sourceMappingURL=actions.js.map